import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { AutomationBookingLink } from "./AutomationBookingLink";
import { useBookingEffReferences } from "./useBookingEffReferences";
import { supabase } from "../../lib/supabase";
import {
  AUTOMATION_OUTCOME_COLORS,
  AUTOMATION_OUTCOME_LABELS,
} from "./automationOutcomeLabels";
import { DocumentCheckTag } from "./DocumentCheckTag";

const { Text } = Typography;

export interface AutomationRun {
  id: string;
  automation_id: string;
  started_at: string;
  finished_at: string | null;
  success: boolean;
  checked_count: number;
  updated_count: number;
  skipped_no_documents_count: number;
  skipped_status_changed_count: number;
  error_count: number;
  error_message: string | null;
}

export interface AutomationRunItem {
  id: string;
  run_id: string;
  booking_id: number | null;
  declaration_id: string | null;
  outcome: string;
  previous_status: string | null;
  new_status: string | null;
  error_message: string | null;
  has_utb: boolean | null;
  has_ttw: boolean | null;
  document_count: number | null;
  created_at: string;
}


interface AutomationRunHistoryProps {
  automationId: string;
  limit?: number;
}

export const AutomationRunHistory: React.FC<AutomationRunHistoryProps> = ({
  automationId,
  limit = 30,
}) => {
  const [runs, setRuns] = useState<AutomationRun[]>([]);
  const [itemsByRun, setItemsByRun] = useState<
    Record<string, AutomationRunItem[]>
  >({});
  const [loading, setLoading] = useState(true);
  const [loadingRunId, setLoadingRunId] = useState<string | null>(null);

  const allBookingIds = useMemo(
    () =>
      Object.values(itemsByRun)
        .flat()
        .map((item) => item.booking_id)
        .filter((id): id is number => id != null),
    [itemsByRun]
  );
  const bookingRefs = useBookingEffReferences(allBookingIds);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("booking_automation_runs")
      .select("*")
      .eq("automation_id", automationId)
      .order("started_at", { ascending: false })
      .limit(limit);

    if (error) {
      message.error("Failed to load automation run history");
      setRuns([]);
    } else {
      setRuns((data as AutomationRun[]) ?? []);
    }
    setLoading(false);
  }, [automationId, limit]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const loadItemsForRun = async (runId: string) => {
    if (itemsByRun[runId]) return;
    setLoadingRunId(runId);
    const { data, error } = await supabase
      .from("booking_automation_run_items")
      .select("*")
      .eq("run_id", runId)
      .order("created_at", { ascending: true });

    if (error) {
      message.error("Failed to load run details");
    } else {
      setItemsByRun((prev) => ({
        ...prev,
        [runId]: (data as AutomationRunItem[]) ?? [],
      }));
    }
    setLoadingRunId(null);
  };

  const runColumns = [
    {
      title: "Started",
      dataIndex: "started_at",
      key: "started_at",
      width: 160,
      render: (v: string) => dayjs(v).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: "Result",
      dataIndex: "success",
      key: "success",
      width: 90,
      render: (success: boolean, record: AutomationRun) =>
        record.finished_at ? (
          <Tag color={success ? "green" : "red"}>
            {success ? "OK" : "Failed"}
          </Tag>
        ) : (
          <Tag color="processing">Running</Tag>
        ),
    },
    {
      title: "Checked",
      dataIndex: "checked_count",
      key: "checked_count",
      width: 80,
    },
    {
      title: "Ready / updated",
      dataIndex: "updated_count",
      key: "updated_count",
      width: 80,
      render: (n: number) =>
        n > 0 ? <Text strong style={{ color: "#52c41a" }}>{n}</Text> : n,
    },
    {
      title: "Waiting docs",
      dataIndex: "skipped_no_documents_count",
      key: "skipped_no_documents_count",
      width: 110,
    },
    {
      title: "Skipped status",
      dataIndex: "skipped_status_changed_count",
      key: "skipped_status_changed_count",
      width: 110,
    },
    {
      title: "Errors",
      dataIndex: "error_count",
      key: "error_count",
      width: 70,
      render: (n: number) =>
        n > 0 ? <Text type="danger">{n}</Text> : n,
    },
    {
      title: "Run error",
      dataIndex: "error_message",
      key: "error_message",
      render: (msg: string | null) =>
        msg ? (
          <Text type="danger" style={{ fontSize: 12 }}>
            {msg}
          </Text>
        ) : (
          "—"
        ),
    },
  ];

  const itemColumns = [
    {
      title: "Booking",
      dataIndex: "booking_id",
      key: "booking_id",
      width: 130,
      render: (id: number | null) =>
        id ? (
          <AutomationBookingLink bookingId={id} refs={bookingRefs} />
        ) : (
          "—"
        ),
    },
    {
      title: "Outcome",
      dataIndex: "outcome",
      key: "outcome",
      width: 180,
      render: (outcome: string) => (
        <Tag color={AUTOMATION_OUTCOME_COLORS[outcome] ?? "default"}>
          {AUTOMATION_OUTCOME_LABELS[outcome] ?? outcome}
        </Tag>
      ),
    },
    {
      title: "From → To",
      key: "status_change",
      width: 220,
      render: (_: unknown, record: AutomationRunItem) => (
        <Text code style={{ fontSize: 12 }}>
          {record.previous_status ?? "?"} → {record.new_status ?? "—"}
        </Text>
      ),
    },
    {
      title: "UTB",
      dataIndex: "has_utb",
      key: "has_utb",
      width: 60,
      render: (v: boolean | null) => <DocumentCheckTag present={v} />,
    },
    {
      title: "TTW",
      dataIndex: "has_ttw",
      key: "has_ttw",
      width: 60,
      render: (v: boolean | null) => <DocumentCheckTag present={v} />,
    },
    {
      title: "Docs",
      dataIndex: "document_count",
      key: "document_count",
      width: 55,
      render: (n: number | null) => (n != null ? n : "—"),
    },
    {
      title: "Declaration",
      dataIndex: "declaration_id",
      key: "declaration_id",
      render: (id: string | null) =>
        id ? (
          <Text copyable={{ text: id }} style={{ fontSize: 11 }}>
            {id.slice(0, 8)}…
          </Text>
        ) : (
          "—"
        ),
    },
    {
      title: "Error",
      dataIndex: "error_message",
      key: "error_message",
      render: (msg: string | null) =>
        msg ? (
          <Text type="danger" style={{ fontSize: 12 }}>
            {msg}
          </Text>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Space style={{ justifyContent: "space-between", width: "100%" }}>
        <Text type="secondary">
          Last {limit} cron executions · per-booking detail on expand
        </Text>
        <Button
          icon={<ReloadOutlined />}
          size="small"
          onClick={loadRuns}
          loading={loading}
        >
          Refresh
        </Button>
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={runColumns}
        dataSource={runs}
        pagination={false}
        expandable={{
          onExpand: (expanded, record) => {
            if (expanded) loadItemsForRun(record.id);
          },
          expandedRowRender: (record) => {
            const items = itemsByRun[record.id];
            if (loadingRunId === record.id) {
              return (
                <div style={{ padding: 16, textAlign: "center" }}>
                  <Spin size="small" />
                </div>
              );
            }
            if (!items?.length) {
              return (
                <Text type="secondary" style={{ padding: 8 }}>
                  No bookings were checked in this run.
                </Text>
              );
            }
            return (
              <Table
                rowKey="id"
                size="small"
                columns={itemColumns}
                dataSource={items}
                pagination={false}
              />
            );
          },
        }}
      />
    </Space>
  );
};
